import { Link } from "@tanstack/react-router";
import { Storefront } from "@phosphor-icons/react";
import { EmptyState, Kpi, KpiRow, PageHeader, Table, TBody, TD, TDNum, TFoot, TH, THead, TR, formatCount } from "@ghezaa/ui";
import { formatDateTime } from "@ghezaa/shell";
import type { TargetCard } from "@/api/client";
import { useToday } from "@/api/queries";
import { change, compact, day, days, inCurrency, money, monthName, monthOf, percent } from "@/lib/format";
import { useCurrentMarket } from "@/lib/market";
import { figure, measureWord } from "@/lib/targets";

/**
 * A person's day: their month so far against the same days of last month, and the customers in their book that need
 * them, the silent and the overdue. Someone who sees every book and holds no code sees the market's.
 */
export function TodayPage() {
  const market = useCurrentMarket();
  const today = useToday(market.code);
  const d = today.data;

  if (d && !d.linked) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title="Today" />
        <EmptyState
          icon={Storefront}
          title="Your account is not linked to a salesperson code yet"
          description="Your figures are the sales BC gives your salesperson code. Ask a Sales admin to link your code to you on the People page."
        />
      </div>
    );
  }

  const thisMonth = d ? monthName(monthOf(d.month.from)) : "";
  const lastMonth = d ? monthName(monthOf(d.before.from)) : "";
  const moved = d ? change(d.now.revenue, d.then.revenue) : null;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Today"
        description={d ? `${d.everyone ? `${market.name}'s` : "Your"} ${thisMonth} so far, against the same days of ${lastMonth}.` : undefined}
      />
      {today.isError && <p className="bg-danger-soft px-3 py-2 text-sm text-danger">Could not load your figures: {today.error.message}</p>}
      {!d && !today.isError && <div className="h-40 bg-surface-2" aria-busy="true" />}
      {d && (
        <>
          <KpiRow cols={4}>
            <Kpi
              label="Revenue"
              value={compact(d.now.revenue)}
              unit={market.currency}
              delta={moved ? `${moved} on ${money(d.then.revenue)} last month` : `${money(d.then.revenue)} last month`}
            />
            <Kpi label="Gross profit" value={compact(d.now.grossProfit)} unit={market.currency} delta={`${percent(d.now.margin)} margin`} />
            <Kpi
              label="Customers who bought"
              value={formatCount(d.now.customers)}
              delta={d.now.linesPerInvoice ? `${d.now.linesPerInvoice} lines an invoice` : `${formatCount(d.then.customers)} last month`}
            />
            <Kpi label="Overdue" value={compact(d.overdue)} unit={market.currency} delta={`${formatCount(d.overdueCustomers)} ${d.overdueCustomers === 1 ? "customer" : "customers"}`} />
          </KpiRow>

          {d.targets.length > 0 && <Targets cards={d.targets} several={d.codes.length > 1} />}

          <div className="grid gap-x-10 gap-y-8 xl:grid-cols-2">
            <section className="flex min-w-0 flex-col gap-3">
              <h2 className="font-display text-xl text-ink">Silent</h2>
              {d.silent.length === 0 ? (
                <p className="text-sm text-ink-3">No customer in {d.everyone ? "the market" : "your book"} has gone quiet.</p>
              ) : (
                <>
                  <Table density="compact">
                    <THead>
                      <TR>
                        <TH>Customer</TH>
                        <TH className="whitespace-nowrap">Last invoice</TH>
                        <TH from="sm" className="text-end whitespace-nowrap">Usually every</TH>
                        <TH from="md" className="text-end whitespace-nowrap">{inCurrency("12 months", market.currency)}</TH>
                      </TR>
                    </THead>
                    <TBody>
                      {d.silent.map((s) => (
                        <TR key={s.no} linked>
                          <TD className="w-full max-w-0">
                            <Link from="/$market" to="/$market/customers/$no" params={{ no: s.no }} className="block truncate underline-offset-2 hover:underline">
                              <bdi>{s.name || s.no}</bdi>
                            </Link>
                          </TD>
                          <TD className="whitespace-nowrap text-xs tabular-nums">
                            {day(s.lastInvoiceOn)}
                            <span className="block text-ink-3">{days(s.daysSince)} ago</span>
                          </TD>
                          <TDNum from="sm">{days(s.usualGapDays)}</TDNum>
                          <TDNum from="md">{money(s.revenue365)}</TDNum>
                        </TR>
                      ))}
                    </TBody>
                  </Table>
                  {d.silentCustomers > d.silent.length && (
                    <Link from="/$market" to="/$market/customers" search={{ silent: true } as never} className="text-sm underline-offset-2 hover:underline">
                      All {formatCount(d.silentCustomers)} silent customers
                    </Link>
                  )}
                </>
              )}
            </section>

            <section className="flex min-w-0 flex-col gap-3">
              <h2 className="font-display text-xl text-ink">Overdue</h2>
              {d.overdueList.length === 0 ? (
                <p className="text-sm text-ink-3">Nothing overdue in {d.everyone ? "the market" : "your book"}.</p>
              ) : (
                <Table density="compact">
                  <THead>
                    <TR>
                      <TH>Customer</TH>
                      <TH className="text-end whitespace-nowrap">{inCurrency("Overdue", market.currency)}</TH>
                      <TH from="sm" className="text-end whitespace-nowrap">{inCurrency("Balance", market.currency)}</TH>
                      <TH from="md" className="text-end whitespace-nowrap">{inCurrency("Credit limit", market.currency)}</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {d.overdueList.map((o) => (
                      <TR key={o.no} linked>
                        <TD className="w-full max-w-0">
                          <Link from="/$market" to="/$market/customers/$no" params={{ no: o.no }} className="block truncate underline-offset-2 hover:underline">
                            <bdi>{o.name || o.no}</bdi>
                          </Link>
                        </TD>
                        <TDNum>{money(o.overdue)}</TDNum>
                        <TDNum from="sm">{money(o.balance)}</TDNum>
                        <TDNum from="md">{o.creditLimit ? money(o.creditLimit) : ""}</TDNum>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              )}
            </section>
          </div>

          <p className="text-xs text-ink-3">
            {d.readAt ? `Sales read from Business Central ${formatDateTime(d.readAt)}.` : "Business Central's sales have not been read yet."} Revenue and cost are before VAT,
            credit memos taken off.
          </p>
        </>
      )}
    </div>
  );
}

/** The month's targets of the person's codes: each figure so far against its target, and where it should be by today. */
function Targets({ cards, several }: { cards: TargetCard[]; several: boolean }) {
  return (
    <section className="flex min-w-0 flex-col gap-3">
      <h2 className="font-display text-xl text-ink">Targets</h2>
      {cards.map((card) => (
        <div key={card.code} className="flex flex-col gap-2">
          {several && <p className="font-mono text-xs text-ink-3">{card.code}</p>}
          <Table density="compact">
            <THead>
              <TR>
                <TH>Measure</TH>
                <TH className="text-end">Target</TH>
                <TH className="text-end whitespace-nowrap">So far</TH>
                <TH bar className="text-end whitespace-nowrap">Of target</TH>
                <TH from="sm" className="text-end whitespace-nowrap">By today</TH>
              </TR>
            </THead>
            <TBody>
              {card.lines.map((l) => (
                <TR key={l.measure}>
                  <TD className="w-full">
                    {measureWord[l.measure]}
                    <span className="text-ink-3"> · {l.weight}%</span>
                  </TD>
                  <TDNum>{figure(l.measure, l.target)}</TDNum>
                  <TDNum>{figure(l.measure, l.actual)}</TDNum>
                  <TDNum bar={l.attainment === null ? 0 : Math.min(1, Math.max(0, l.attainment))}>{percent(l.attainment)}</TDNum>
                  <TDNum from="sm">{l.pace === null ? "" : figure(l.measure, l.pace)}</TDNum>
                </TR>
              ))}
            </TBody>
            <TFoot>
              <TR>
                <TD>Overall</TD>
                <TDNum />
                <TDNum />
                <TDNum bar={null}>{percent(card.overall)}</TDNum>
                <TDNum from="sm" />
              </TR>
            </TFoot>
          </Table>
        </div>
      ))}
    </section>
  );
}
